import { selectedPlaybookSchema } from "@repo/app-types/agents/selectedPlaybook";
import { configurationTestResultIdSchema } from "@repo/app-types/entities/configurationTestResult";
import {
  buildPlaybookProposalResourceKey,
  buildTeammateAgentTileProposalResourceKey,
  type CopilotProposalSummary,
} from "@repo/app-types/entities/copilotProposal";
import { teammateIdSchema } from "@repo/app-types/entities/teammate";
import { agentInstructionSectionCandidateDraftSchema } from "@repo/app-types/entities/voiceAgentConfigurationTools";
import type { UIMessage } from "ai";
import { z } from "zod";
import type { EditorFocus } from "./editorProposalsSlice.js";

/** How the AI SDK prefixes a statically-declared tool part's `type`. */
const TOOL_PART_TYPE_PREFIX = "tool-";

/**
 * The proposals one assistant turn put up for review, in the order the agent
 * made them.
 *
 * Each turn lists what it proposed rather than everything outstanding, which
 * is what keeps the transcript readable as a history: a proposal appears once,
 * where it was made, and its row carries whatever became of it. A turn that
 * proposed nothing — no proposal calls, or only tests that measured a node
 * without changing it — gets no list.
 */
export function selectTurnProposals(
  message: UIMessage,
  proposalsById: ReadonlyMap<string, CopilotProposalSummary>,
): readonly CopilotProposalSummary[] {
  if (message.role !== "assistant") {
    return [];
  }

  // One row per target: a turn that revised its own proposal shows the last
  // one it made. Earlier turns keep their own rows.
  const byResourceKey = new Map<string, CopilotProposalSummary>();
  for (const proposalId of readProposalIds(message)) {
    const proposal = proposalsById.get(proposalId);
    if (!proposal) {
      continue;
    }
    byResourceKey.set(proposal.resource_key, proposal);
  }
  return [...byResourceKey.values()];
}

/**
 * The tests a turn's test tools ran, read off each completed call's output.
 * Unlike a proposal, a test has no live status to look up. Two kinds: a
 * `check` is one saved configuration test result that opens in the resource's
 * editor, so it carries what labels the row and the revision it measured; a
 * `testCaseRun` is a run of a voice agent's saved test cases that opens the
 * Testing tab, so it carries only the per-case outcomes that label the row,
 * as they stood when the tool stopped waiting. Each tool returns its own
 * shape, so each has its own arm, tagged with its kind and the resource it
 * measured.
 */
const playbookTurnCheckSchema = z
  .object({
    test_result_id: configurationTestResultIdSchema,
    playbook: selectedPlaybookSchema,
    playbook_node_id: z.string(),
    playbook_revision_id: z.string(),
    node_name: z.string(),
  })
  .transform((result) => ({
    kind: "check" as const,
    resource_type: "playbook" as const,
    ...result,
  }));

const teammateAgentTurnCheckSchema = z
  .object({
    test_result_id: configurationTestResultIdSchema,
    teammate_agent_id: teammateIdSchema,
    teammate_agent_config_revision_id: z.string().nullable(),
    candidate_draft: agentInstructionSectionCandidateDraftSchema.nullable(),
  })
  .transform((result) => ({
    kind: "check" as const,
    resource_type: "teammateAgent" as const,
    ...result,
  }));

const teammateAgentTurnTestCaseRunSchema = z
  .object({
    test_run_id: z.string(),
    teammate_agent_id: teammateIdSchema,
    results: z.array(
      z.object({
        test_spec_id: z.string(),
        status: z.enum(["in_progress", "done", "error"]),
        passed: z.boolean().nullable(),
      }),
    ),
  })
  .transform((run) => ({
    kind: "testCaseRun" as const,
    resource_type: "teammateAgent" as const,
    ...run,
  }));

const turnTestSchema = z.union([
  playbookTurnCheckSchema,
  teammateAgentTurnCheckSchema,
  teammateAgentTurnTestCaseRunSchema,
]);

export type TurnTest = z.infer<typeof turnTestSchema>;
export type TurnCheck = Extract<TurnTest, { kind: "check" }>;
export type TurnTestCaseRun = Extract<TurnTest, { kind: "testCaseRun" }>;
export type PlaybookTurnTest = Extract<TurnTest, { resource_type: "playbook" }>;
export type TeammateAgentTurnTest = Extract<
  TurnTest,
  { resource_type: "teammateAgent" }
>;

/** The id a test's row carries, which is also what opening the row navigates to. */
export function readTurnTestId(test: TurnTest): string {
  switch (test.kind) {
    case "check":
      return test.test_result_id;
    case "testCaseRun":
      return test.test_run_id;
  }
}

/**
 * The tests one assistant turn ran, in the order it ran them. A proposal's
 * output carries neither a test result id nor a test run id, so proposals
 * never show here. A check the same turn turned into a proposal is left out:
 * the proposal carries the same grades on its own row (see
 * `checkIsCheckReusedByProposal`).
 */
export function selectTurnTests(
  message: UIMessage,
  proposalsById: ReadonlyMap<string, CopilotProposalSummary>,
): readonly TurnTest[] {
  const proposals = selectTurnProposals(message, proposalsById);
  return readTurnTests(message).filter(
    (test) =>
      test.kind !== "check" || !checkIsCheckReusedByProposal(test, proposals),
  );
}

/** Every test a turn's completed tool calls returned, in call order. */
function readTurnTests(message: UIMessage): readonly TurnTest[] {
  if (message.role !== "assistant") {
    return [];
  }
  const tests: TurnTest[] = [];
  for (const part of message.parts) {
    if (!isCompletedToolPart(part)) {
      continue;
    }
    const parsed = turnTestSchema.safeParse(
      "output" in part ? part.output : undefined,
    );
    if (parsed.success) {
      tests.push(parsed.data);
    }
  }
  return tests;
}

/** Whether a proposal the same turn made carries this check's grades. */
function checkIsCheckReusedByProposal(
  check: TurnCheck,
  proposals: readonly CopilotProposalSummary[],
): boolean {
  switch (check.resource_type) {
    case "playbook":
      return checkIsPlaybookCheckReusedByProposal(check, proposals);
    case "teammateAgent":
      return checkIsTeammateAgentCheckReusedByProposal(check, proposals);
  }
}

/** A playbook node check is reused by a proposal for that node. */
function checkIsPlaybookCheckReusedByProposal(
  check: Extract<TurnCheck, { resource_type: "playbook" }>,
  proposals: readonly CopilotProposalSummary[],
): boolean {
  const nodeKey = buildPlaybookProposalResourceKey(
    check.playbook.id,
    check.playbook_node_id,
  );
  return proposals.some((proposal) => proposal.resource_key === nodeKey);
}

/**
 * A voice agent instruction check is reused by an instruction proposal for
 * that agent. A test case proposal does not come from a check, so a check
 * run alongside one keeps its row.
 */
function checkIsTeammateAgentCheckReusedByProposal(
  check: Extract<TurnCheck, { resource_type: "teammateAgent" }>,
  proposals: readonly CopilotProposalSummary[],
): boolean {
  return proposals.some(
    (proposal) =>
      proposal.resource_type === "teammateAgent" &&
      proposal.kind === "update_instruction_section" &&
      proposal.resource.id === check.teammate_agent_id,
  );
}

const UPDATE_PROPOSAL_KINDS = {
  signal: "update_signal",
  automation: "update_automation",
  training: "update_training",
  tool: "update_tool",
} as const;

export type EditorRowToOpen =
  | { kind: "proposal"; proposal: CopilotProposalSummary }
  | { kind: "check"; check: TurnCheck };

/**
 * The row of a finished turn the editor opens without a click: the pending
 * proposal for what the editor has selected, else the last check that
 * measured it. A voice agent's check measures the whole agent, so it matches
 * on any tile. A test case run opens the Testing tab, not an editor, so it
 * waits for a click.
 *
 * A proposal waits for a click while the editor has unsaved edits; a check
 * only fills the results pane. A signal, automation, training or tool editor
 * puts a proposal into its draft, which makes the draft unsaved, so once it
 * holds one the next proposal opens regardless.
 */
export function selectEditorRowToOpen(
  message: UIMessage,
  proposalsById: ReadonlyMap<string, CopilotProposalSummary>,
  focus: EditorFocus,
): EditorRowToOpen | undefined {
  const proposals = selectTurnProposals(message, proposalsById).filter(
    (proposal) =>
      proposal.status === "pending" &&
      proposal.resource_type === focus.resource_type,
  );
  const checks = selectTurnTests(message, proposalsById).filter(
    (test) => test.kind === "check",
  );

  let proposal: CopilotProposalSummary | undefined;
  let check: TurnCheck | undefined;
  let isProposalHeld = focus.hasUnsavedChanges;
  switch (focus.resource_type) {
    case "playbook": {
      const nodeKey = buildPlaybookProposalResourceKey(
        focus.playbookId,
        focus.playbookNodeId,
      );
      proposal = proposals.find(
        (candidate) => candidate.resource_key === nodeKey,
      );
      check = checks.findLast(
        (candidate) =>
          candidate.resource_type === "playbook" &&
          candidate.playbook.id === focus.playbookId &&
          candidate.playbook_node_id === focus.playbookNodeId,
      );
      break;
    }
    case "teammateAgent": {
      const tileKey = buildTeammateAgentTileProposalResourceKey(
        focus.teammateAgentId,
        focus.tile,
      );
      proposal = proposals.find(
        (candidate) => candidate.resource_key === tileKey,
      );
      check = checks.findLast(
        (candidate) =>
          candidate.resource_type === "teammateAgent" &&
          candidate.teammate_agent_id === focus.teammateAgentId,
      );
      break;
    }
    case "signal":
    case "automation":
    case "training":
    case "tool":
      // A create proposal belongs to the page that creates the resource.
      proposal = proposals.find(
        (candidate) =>
          candidate.resource.id === focus.resourceId &&
          candidate.kind === UPDATE_PROPOSAL_KINDS[focus.resource_type],
      );
      isProposalHeld = focus.hasUnsavedChanges && !focus.hasLinkedProposal;
      break;
  }

  if (proposal) {
    return isProposalHeld ? undefined : { kind: "proposal", proposal };
  }
  return check ? { kind: "check", check } : undefined;
}

/**
 * The test case runs the thread's turns have finished. A run writes its
 * results server-side, which no client mutation observes, so the chat watches
 * these to refetch the agents' test results.
 */
export function selectThreadTestCaseRuns(
  messages: readonly UIMessage[],
): readonly TurnTestCaseRun[] {
  return messages.flatMap((message) =>
    readTurnTests(message).filter((test) => test.kind === "testCaseRun"),
  );
}

/**
 * How many proposals the thread's turns have made. The agent writes proposals
 * server-side mid-turn, which no client mutation observes, so the chat watches
 * this count to know when its copy of them is stale.
 */
export function countTurnProposals(messages: readonly UIMessage[]): number {
  let total = 0;
  for (const message of messages) {
    if (message.role === "assistant") {
      total += readProposalIds(message).length;
    }
  }
  return total;
}

/**
 * Whether a message part is a finished tool call. Tool parts are typed either
 * as `dynamic-tool` or as `tool-${name}`, and both carry `state`.
 */
function isCompletedToolPart(part: UIMessage["parts"][number]): boolean {
  const isTool =
    part.type.startsWith(TOOL_PART_TYPE_PREFIX) || part.type === "dynamic-tool";
  return isTool && "state" in part && part.state === "output-available";
}

/**
 * The `proposal_id`s a turn's proposal tools returned. A proposal is recognized
 * by the shape of a completed call's output — a string `proposal_id`, or a
 * `proposals` list of them from the voice agent instructions tool, which
 * writes one row per tile so the tiles share the checks of the combined
 * candidate — rather than by the tool's name, so the list keeps working
 * across tool renames and for any future proposal-writing tool. A proposal is
 * persisted only for a real change, so every returned `proposal_id` is a
 * suggestion to show.
 */
function readProposalIds(message: UIMessage): readonly string[] {
  const ids: string[] = [];
  for (const part of message.parts) {
    if (!isCompletedToolPart(part)) {
      continue;
    }
    const output = ("output" in part ? part.output : undefined) as
      | { proposal_id?: unknown; proposals?: unknown }
      | undefined;
    if (typeof output?.proposal_id === "string") {
      ids.push(output.proposal_id);
    }
    if (Array.isArray(output?.proposals)) {
      for (const entry of output.proposals as { proposal_id?: unknown }[]) {
        if (typeof entry?.proposal_id === "string") {
          ids.push(entry.proposal_id);
        }
      }
    }
  }
  return ids;
}
